import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import html2canvas from 'html2canvas';
import { API_URL } from '../utils/api';
import { Dialog, DialogContent } from './ui/dialog';
import { Button } from './ui/button';
import {
  Sun, Broadcast, Drop, UserPlus, MapPin, ChartLineUp, IdentificationCard, ArrowsClockwise, DownloadSimple,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const authHeaders = () => {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const fmtInt = (n) => Number(n || 0).toLocaleString();
const fmt2 = (n) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const MetricTile = ({ icon: Icon, label, value, sub, testid }) => (
  <div className="bg-[#FCFAF6] border border-[#EDE6D8] rounded-xl p-4" data-testid={testid}>
    <div className="flex items-center gap-2 mb-2">
      <Icon size={16} className="text-[#0A2540]" />
      <span className="text-[11px] uppercase tracking-wider text-[#78716C] font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
        {label}
      </span>
    </div>
    <div className="text-xl font-bold text-[#0A2540] whitespace-nowrap" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>{value}</div>
    {sub && <div className="text-xs text-[#78716C] mt-1">{sub}</div>}
  </div>
);

export const DayStarterPopup = () => {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const captureRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/day-starter`, { withCredentials: true, headers: authHeaders() });
        if (!cancelled && res.data?.show) {
          setData(res.data);
          setOpen(true);
        }
      } catch {
        /* silent — popup is best-effort */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const ack = () => {
    axios.post(`${API_URL}/api/day-starter/ack`, {}, { withCredentials: true, headers: authHeaders() }).catch(() => {});
  };

  const handleClose = () => {
    setOpen(false);
    ack();
  };

  const handleDownload = async () => {
    if (!captureRef.current) return;
    setDownloading(true);
    try {
      const canvas = await html2canvas(captureRef.current, {
        backgroundColor: '#FFFFFF',
        scale: 2,
        useCORS: true,
      });
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
      if (!blob) throw new Error('capture failed');
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `day-starter-${data?.date || 'report'}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success('Day Starter screenshot downloaded');
    } catch (e) {
      toast.error('Could not capture screenshot');
    } finally {
      setDownloading(false);
    }
  };

  if (!data) return null;

  const m = data.metrics || {};
  const cur = data.currency ? ` ${data.currency}` : '';
  const dateLabel = new Date(`${data.date}T00:00:00`).toLocaleDateString(undefined, {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  });

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <DialogContent className="max-w-3xl p-0 overflow-hidden" data-testid="day-starter-popup">
        <div ref={captureRef} className="bg-white pb-2">
          <div className="bg-[#0A2540] px-6 py-5">
            <div className="flex items-center gap-2">
              <Sun size={22} weight="fill" className="text-[#F5C542]" />
              <h2 className="text-lg font-bold text-white" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                Day Starter
              </h2>
            </div>
            <p className="text-sm text-white/70 mt-1" data-testid="day-starter-date">
              Yesterday at a glance: {dateLabel}
            </p>
          </div>

          <div className="p-6">
            {/* Total Revenue hero */}
            <div className="bg-[#0A2540]/[0.04] border border-[#EDE6D8] rounded-xl p-4 mb-3" data-testid="day-starter-revenue">
              <div className="flex items-center gap-2 mb-1">
                <ChartLineUp size={16} className="text-[#0A2540]" />
                <span className="text-[11px] uppercase tracking-wider text-[#78716C] font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                  Total Revenue
                </span>
              </div>
              <div className="text-3xl font-bold text-[#0A2540] whitespace-nowrap" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                {fmt2(m.revenue?.total)}{cur}
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <MetricTile
                icon={Broadcast}
                label="Recharges"
                value={`${fmt2(m.revenue?.recharges)}${cur}`}
                testid="day-starter-recharges"
              />
              <MetricTile
                icon={IdentificationCard}
                label="Sub Fees"
                value={`${fmt2(m.revenue?.subs_fee)}${cur}`}
                testid="day-starter-subs-fee"
              />
              <MetricTile
                icon={ArrowsClockwise}
                label="Renewal Fees"
                value={`${fmt2(m.revenue?.renewal_fee)}${cur}`}
                testid="day-starter-renewal-fee"
              />
            </div>

            <div className="grid grid-cols-2 gap-3 mt-3">
              <MetricTile
                icon={UserPlus}
                label="New Subscribers"
                value={fmtInt(m.new_subscribers)}
                testid="day-starter-new-subs"
              />
              <MetricTile
                icon={Drop}
                label="Litres Sold"
                value={`${fmtInt(m.litres_sold)} L`}
                testid="day-starter-litres"
              />
            </div>

            <div className="mt-3">
              <div className="bg-[#FCFAF6] border border-[#EDE6D8] rounded-lg px-4 py-2.5" data-testid="day-starter-top-campus">
                <div className="flex items-center gap-2 text-sm text-[#78716C]">
                  <MapPin size={15} className="text-[#0A2540]" />
                  Top campus by revenue
                </div>
                <div className="mt-1 text-sm font-semibold text-[#0A2540] whitespace-nowrap" data-testid="day-starter-top-campus-name">
                  {m.top_campus ? m.top_campus.name : '—'}
                </div>
                {m.top_campus && (
                  <div className="mt-0.5 text-right text-sm font-semibold text-[#0A2540] whitespace-nowrap" data-testid="day-starter-top-campus-revenue">
                    {fmt2(m.top_campus.revenue)}{cur}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="px-6 pb-6 flex gap-3">
          <Button
            onClick={handleDownload}
            disabled={downloading}
            variant="outline"
            className="flex-1 border-[#EDE6D8] text-[#0A2540] hover:bg-[#FCFAF6] rounded-lg"
            data-testid="day-starter-download-btn"
          >
            <DownloadSimple size={16} className="mr-2" />
            {downloading ? 'Preparing…' : 'Download screenshot'}
          </Button>
          <Button
            onClick={handleClose}
            className="flex-1 bg-[#0A2540] hover:bg-[#0A2540]/90 text-white rounded-lg"
            data-testid="day-starter-close-btn"
          >
            Got it, Let&apos;s go
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DayStarterPopup;
